import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnDestroy, OnInit } from '@angular/core';

import { BehaviorSubject, combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import {
    catchError,
    debounceTime,
    filter,
    map,
    shareReplay,
    switchMap,
    take,
    takeUntil,
    tap,
    withLatestFrom
} from 'rxjs/operators';

import { animate, style, transition, trigger } from '@angular/animations';
import { LanguageModule } from '@language';
import { ApiQuery, Restriction } from '../../../core/api/entities/api-query.entity';
import { BaseContainer } from '../../../core/common/containers/base.abstract.container';
import { BaseNestedFieldDefinition, DottedKey } from '../../../core/field-definitions/entities/field-definition.entity';
import { FieldDefinitionHelper } from '../../../core/field-definitions/helpers/field-definition.helper';
import { ShFieldDefinitionService } from '../../../core/field-definitions/services/field-definition.service';
import { ExtendedFacetProfileField } from '../../../core/profiles/entities/extended-profile-field.entity';
import { ExtendedFacetProfile } from '../../../core/profiles/entities/extended-profile.entity';
import { FacetProfileField } from '../../../core/profiles/entities/profile-field.entity';
import { FacetProfile } from '../../../core/profiles/entities/profile.entity';
import { ShUrlQueryService } from '../../../core/query/services/url-query.service';
import { DialogConfig } from '../../../dynamic-dialog/entities/dialog-config.entity';
import { DialogRef } from '../../../dynamic-dialog/entities/dialog-ref.class';
import { ShDynamicDialogService } from '../../../dynamic-dialog/services/dynamic-dialog.service';
import { DialogStyle } from '../../../elements/components/dialog/entities/dialog.entity';
import { ShFacetListComponent } from '../../../elements/components/facet-list/facet-list.component';
import { ShFacetPlaceHolderComponent } from '../../../elements/components/facet-placeholder/facet-placeholder.component';
import { ShFacetSummaryComponent } from '../../../elements/components/facet-summary/facet-summary.component';
import {
    ShFacet,
    ShSelectableFacetValue,
    ShSelectedFacetValue
} from '../../../elements/components/facet/entities/facet.entity';
import { LoadingBehaviorSubject } from '../../../helpers/loading-behavior-subject.helper';
import { COMPLETABLE_TASK_FACET } from '../../constants/virtual-facets.constants';
import { ShTaskFacetDataService } from '../../data-services/task-facet.data-service';
import { FacetSelectionType } from '../../entities/facet-request.entity';
import {
    ProfileFacetSearchDialogOutput,
    ProfileFacetSearchDialogOutputSuccess,
    TaskFacetSearchDialogConfig
} from '../../entities/facet-search-dialog.entity';
import { ExtractOnlyCompletableValue } from '../../helpers/task-facet.helper';
import { ShFacetValueTranslationService } from '../../services/facet-value-translation.service';
import { ShTaskFacetSearchDialogComponent } from '../task-facet-search-dialog/task-facet-search-dialog.component';

@Component({
    standalone: true,
    selector: 'sh-task-facet-list',
    templateUrl: './task-facet-list-container.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, ShFacetSummaryComponent, ShFacetListComponent, ShFacetPlaceHolderComponent, LanguageModule],
    providers: [],
    animations: [
        // Prevent from appearing for 10ms
        trigger('fadeIn', [transition(':enter', [style({ display: 'none' }), animate('0ms 10ms', style({}))])]),
        // Prevent from disappearing for 10ms
        trigger('fadeOut', [transition(':leave', [style({}), animate('0ms 10ms', style({}))])])
    ]
})
export class ShTaskFacetListContainerComponent extends BaseContainer implements OnInit, OnDestroy {
    private query$!: Observable<ApiQuery>;

    // Current facet profile
    public facetProfile$: ReplaySubject<ExtendedFacetProfile> = new ReplaySubject<ExtendedFacetProfile>(1);
    private extendedFacetProfile$: Observable<ExtendedFacetProfile>;

    // Currently selected facets
    public selectedFacets$!: Observable<ShSelectedFacetValue[]>;

    // Facet results with selected filtered out
    public selectableFacets$!: Observable<ShFacet[]>;

    public restrictions$: BehaviorSubject<Restriction | undefined> = new BehaviorSubject<Restriction | undefined>(
        undefined
    );

    public readonly facetsLoading$: LoadingBehaviorSubject = new LoadingBehaviorSubject();

    protected readonly facetsService: ShTaskFacetDataService = inject(ShTaskFacetDataService);

    @Input() set facetProfile(facetProfile: ExtendedFacetProfile) {
        if (facetProfile) {
            this.facetProfile$.next(facetProfile);
        }
    }

    /**
     * Pass background restricting query for page-results.
     * Ex. Filtering on published Records and Collections
     */
    @Input() set queryRestrictions(queryRestrictions: Restriction) {
        this.restrictions$.next(queryRestrictions);
    }

    @Input() includeOnlyCompletableFacet = false;

    constructor(
        private readonly urlQueryService: ShUrlQueryService,
        private readonly fieldDefinitionService: ShFieldDefinitionService,
        private readonly facetValueTranslateService: ShFacetValueTranslationService,
        private readonly dialogService: ShDynamicDialogService
    ) {
        super();
    }

    public ngOnInit(): void {
        this.initExtendedProfileStream();
        this.initQueryStream();
        this.initFacetSelectedStream();
        this.initFacetsStream();
    }

    public ngOnDestroy(): void {
        super.ngOnDestroy();
        this.facetsLoading$.complete();
    }

    // #region Extend profile
    // TO IMPROVE: Add ExtendedProfileService to Stylehaven and just pass profile-id/tag into component
    // OR API should provide at least type of field
    private initExtendedProfileStream() {
        this.extendedFacetProfile$ = this.facetProfile$.pipe(
            switchMap(facetProfile => {
                const fielddefinitions$: Observable<BaseNestedFieldDefinition[]> =
                    this.fieldDefinitionService.getByProfileIds(
                        [facetProfile.Id],
                        facetProfile.Fields.map(field => field.DottedKey)
                    );
                return fielddefinitions$.pipe(
                    map(fieldDefinitions => this.extendProfile(facetProfile, fieldDefinitions))
                );
            })
        );
    }

    private extendProfile(profile: FacetProfile, fieldDefinitions: BaseNestedFieldDefinition[]): ExtendedFacetProfile {
        if (!profile.Fields || profile.Fields.length === 0) {
            return profile as ExtendedFacetProfile;
        }

        const extendedProfileFields: ExtendedFacetProfileField[] = profile.Fields.map(field =>
            this.extendProfileField(field, fieldDefinitions)
        );
        return {
            ...profile,
            Fields: extendedProfileFields
        };
    }

    private extendProfileField(
        profileField: FacetProfileField,
        fieldDefinitions: BaseNestedFieldDefinition[]
    ): ExtendedFacetProfileField {
        const rootKey: DottedKey | undefined = FieldDefinitionHelper.getDottedKeyRoot(profileField.DottedKey);
        const rootFieldDefinition: BaseNestedFieldDefinition | undefined = fieldDefinitions.find(
            fieldDefinition => fieldDefinition.DottedKey === rootKey
        );

        const profileFieldDefinition: BaseNestedFieldDefinition | undefined = rootFieldDefinition
            ? FieldDefinitionHelper.getNestedByDottedKey(rootFieldDefinition, profileField.DottedKey)
            : undefined;

        if (!profileFieldDefinition || !rootFieldDefinition) {
            console.warn(`FieldDefinition or root of ${profileField.DottedKey} was not found`);
        }

        return {
            ...profileField,
            FieldDefinition: profileFieldDefinition
        } as ExtendedFacetProfileField;
    }
    // #endregion

    private initQueryStream(): void {
        this.query$ = combineLatest([
            this.urlQueryService.getStructuredQuery(),
            this.restrictions$,
            this.urlQueryService.getSelectedFacets() // Needed to trigger when 'My tasks' filter is removed, since this is not exposed in StructuredQuery
        ]).pipe(
            map(([query, restrictions]) => {
                return new ApiQuery(query, restrictions);
            })
        );
    }

    private initFacetSelectedStream(): void {
        this.selectedFacets$ = combineLatest([
            this.urlQueryService.getSelectedFacets(),
            this.extendedFacetProfile$
        ]).pipe(
            takeUntil(this.destroy$), // Switching to a page with cached facets can make this trigger and cause an error
            map(([selectedFacets, facetProfile]) =>
                this.translateSelectedFacetLabels(selectedFacets, facetProfile.Fields).sort((a, b) =>
                    a.facetLabel.localeCompare(b.facetLabel)
                )
            ),
            shareReplay(1)
        );
    }

    private translateSelectedFacetLabels(
        selectedFacets: ShSelectedFacetValue[],
        facetProfileFields: ExtendedFacetProfileField[]
    ): ShSelectedFacetValue[] {
        const translatedSelectedFacets: ShSelectedFacetValue[] = [];

        selectedFacets.forEach(selected => {
            const onlyCompletableFacet = this.getOnlyCompletableFacet();
            if (selected.key === onlyCompletableFacet.key) {
                translatedSelectedFacets.push({
                    ...selected,
                    facetLabel: onlyCompletableFacet.label,
                    valueLabel: this.translateService.instant(`facets.tasks.only-completable.values.${selected.value}`)
                });
            } else {
                const matchingFacetProfileField = facetProfileFields.find(
                    facetProfileField => selected.key === facetProfileField.DottedKey
                );

                if (!matchingFacetProfileField) {
                    // Ignore facets with no matching FacetProfileField
                    console.error(`Facet with key ${selected.key} not found in Facet Profile`);
                    return;
                }

                translatedSelectedFacets.push({
                    ...selected,
                    facetLabel: matchingFacetProfileField.DisplayLabel,
                    valueLabel: this.facetValueTranslateService.translateValue(
                        matchingFacetProfileField,
                        selected.value,
                        selected.missing ?? false
                    )
                });
            }
        });

        return translatedSelectedFacets;
    }

    private initFacetsStream(): void {
        this.facetsLoading$.startLoading();

        const apiFacets$: Observable<ShFacet[]> = combineLatest([this.query$, this.extendedFacetProfile$]).pipe(
            // MED-4283: prevent too frequent calling facet API
            debounceTime(100),
            tap(() => this.facetsLoading$.startLoading()),
            tap(() => this.setError(false)),
            switchMap(([query, facetProfile]) => {
                const onlyCompletable = this.getOnlyCompletable(query);
                return this.facetsService
                    .getProfileFacets(query, facetProfile.Id, facetProfile.Fields, onlyCompletable)
                    .pipe(catchError(error => this.onLoadError(error)));
            }),
            withLatestFrom(this.selectedFacets$, this.extendedFacetProfile$),
            map(([facets, selectedFacets, facetProfile]) => {
                const allFacets = this.addCompletableFacetIfNeeded(facets, selectedFacets);
                const translatedFacets: ShFacet[] = this.translateFacets(allFacets, facetProfile);
                return this.hideFacets(translatedFacets, selectedFacets, facetProfile);
            }),
            catchError(error => this.onLoadError(error)),
            takeUntil(this.destroy$),
            shareReplay(1)
        );

        this.selectableFacets$ = combineLatest([apiFacets$, this.selectedFacets$]).pipe(
            tap(() => this.facetsLoading$.startLoading()),
            map(([facets, selectedFacets]) => this.filterNonSelectedValues(facets, selectedFacets)),
            tap(() => this.facetsLoading$.stopLoading())
        );
    }

    private onLoadError(error: Error): Observable<ShFacet[]> {
        console.error('TaskFacetList:: An error occured loading the facets', error);

        this.setError(true);
        this.facetsLoading$.stopLoading();

        return of([]);
    }

    private translateFacets(facets: ShFacet[], facetProfile: ExtendedFacetProfile): ShFacet[] {
        return facets.map(facet => {
            const facetField: ExtendedFacetProfileField | undefined = facetProfile.Fields.find(
                field => field.DottedKey === facet.key
            );
            const translatedValues: ShSelectableFacetValue[] = facet.values.map(facetValue => ({
                ...facetValue,

                label: facetField
                    ? this.facetValueTranslateService.translateValue(
                          facetField,
                          facetValue.value,
                          facetValue.missing ?? false
                      )
                    : facetValue.label
            }));

            return {
                ...facet,
                values: translatedValues
            };
        });
    }

    private hideFacets(
        facets: ShFacet[],
        selectedFacets: ShSelectedFacetValue[],
        facetProfile: FacetProfile
    ): ShFacet[] {
        const filteredFacets: ShFacet[] = [];

        facets.forEach(facet => {
            const hasValues: boolean = facet.values.length > 0;
            const selectedFacetsOfFacet: ShSelectedFacetValue[] = selectedFacets.filter(
                selected => selected.key === facet.key
            );

            const hasSelected: boolean = selectedFacetsOfFacet.length > 0;

            // Hide empty facets
            const hideEmpty: boolean =
                facetProfile.Fields.find(field => field.DottedKey === facet.key)?.HideEmpty ?? true;

            if (!hideEmpty || hasValues || hasSelected) {
                filteredFacets.push(facet);
            }
        });

        return filteredFacets;
    }

    private addCompletableFacetIfNeeded(facets: ShFacet[], selectedFacets: ShSelectedFacetValue[]): ShFacet[] {
        if (!this.includeOnlyCompletableFacet) {
            return facets;
        }
        const selected = selectedFacets.find(value => value.key === COMPLETABLE_TASK_FACET);
        if (selected !== undefined) {
            return facets;
        }
        const completableFacet: ShFacet = this.getOnlyCompletableFacet();
        facets.push(completableFacet);
        return facets;
    }

    private filterNonSelectedValues(facets: ShFacet[], selectedFacets: ShSelectedFacetValue[]): ShFacet[] {
        const filteredFacets: ShFacet[] = facets.map(facet => {
            const selectedFacetsValues: string[] = selectedFacets
                .filter(selected => selected.key === facet.key)
                .map(selected => selected.value);

            const filteredValues: ShSelectableFacetValue[] = facet.values.filter(
                selectableValue => !selectedFacetsValues.includes(selectableValue.value)
            );

            return {
                ...facet,
                values: filteredValues
            };
        });

        return filteredFacets;
    }

    // #region List Interactions
    public onClear(): void {
        this.urlQueryService.clearFacetQuery();
    }

    public onRemoveFacet(facet: ShSelectedFacetValue): void {
        this.urlQueryService.removeFacetValue(facet);
    }

    public onFacetSelected(facet: ShSelectedFacetValue): void {
        this.urlQueryService.addFacetValue(facet);
    }

    public onLoadMore(facet: ShFacet, facetProfile: ExtendedFacetProfile): void {
        const facetProfileField = facetProfile.Fields.find(field => field.DottedKey === facet.key);

        if (!facetProfileField) {
            console.error(`Selected facet ${facet.key} not found in facet-profile`);
            return;
        }

        of([])
            .pipe(
                withLatestFrom(this.selectedFacets$, this.query$),
                switchMap(([_, selected, query]) =>
                    this.openFacetsDialog(facetProfileField, facetProfile.Fields, selected, query, facetProfile.Id)
                ),
                takeUntil(this.destroy$)
            )
            .subscribe((dialogOutput: ProfileFacetSearchDialogOutputSuccess) => {
                this.urlQueryService.replaceFacetValues(facet, dialogOutput.selectedValues || []);
            });
    }
    // #endregion

    // #region FacetDialog
    private openFacetsDialog(
        facetProfileField: ExtendedFacetProfileField,
        allFacetProfileFields: FacetProfileField[],
        selected: ShSelectedFacetValue[],
        query: ApiQuery,
        facetProfileId: string
    ): Observable<ProfileFacetSearchDialogOutputSuccess> {
        const apply: string = this.translateService.instant('actions.common.apply');
        const cancel: string = this.translateService.instant('actions.common.cancel');

        const config: DialogConfig<TaskFacetSearchDialogConfig> = {
            title: facetProfileField.DisplayLabel,
            data: {
                cancel,
                apply,
                facetProfileField,
                facetProfileId,
                allFacetProfileFields,
                selectedFacets: selected,
                query,
                onlyCompletable: this.getOnlyCompletable(query)
            },
            options: {
                style: DialogStyle.Wide
            }
        };

        const ref: DialogRef<ProfileFacetSearchDialogOutput> = this.dialogService.open(
            ShTaskFacetSearchDialogComponent,
            config
        );

        return ref.afterClosed$.pipe(
            take(1),
            takeUntil(this.destroy$),
            filter(
                (output: ProfileFacetSearchDialogOutput): output is ProfileFacetSearchDialogOutputSuccess =>
                    output.type === 'success' && output.hasOwnProperty('selectedValues')
            ),
            shareReplay(1)
        );
    }
    // #endregion

    // #region OnlyCompletable

    private getOnlyCompletableFacet(): ShFacet {
        return {
            key: COMPLETABLE_TASK_FACET,
            label: this.translateService.instant('facets.tasks.only-completable.label'),
            hasMoreValues: false,
            values: [
                {
                    value: 'true',
                    label: this.translateService.instant('facets.tasks.only-completable.values.true'),
                    count: undefined as never as number,
                    selectionType: FacetSelectionType.MUST
                },
                {
                    value: 'false',
                    label: this.translateService.instant('facets.tasks.only-completable.values.false'),
                    count: undefined as never as number,
                    selectionType: FacetSelectionType.MUST
                }
            ],
            selectionType: FacetSelectionType.MUST
        };
    }

    private getOnlyCompletable(query: ApiQuery) {
        // Default value of onlyCompletable is opposite of showOnlyCompletable:
        // If we don't show the completable filter (mediahaven case, value = false): user can only see completable tasks (true)
        // If we do show the completable filter (digihaven case, value = true): we show all tasks if the user has deselected the filter (false)
        return ExtractOnlyCompletableValue(query, !this.includeOnlyCompletableFacet);
    }
    // #endregion
}
